import {
  BarChart3,
  Bot,
  Inbox,
  LifeBuoy,
  Plus,
  Send,
  Settings,
  SlidersHorizontal,
  Ticket,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { useSession } from "@/lib/session";
import { useProjects } from "@/lib/projects";
import { StatusDot } from "@/components/ui";
import { AgentFooter } from "./AgentFooter";
import { CompanySwitcher } from "./CompanySwitcher";
import { NotificationBell } from "./NotificationBell";

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  // extra path prefixes that should also mark this item active (deep links)
  alsoActive?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", labelKey: "nav.inbox", icon: Inbox, end: true, alsoActive: ["/conversations"] },
  { to: "/tickets", labelKey: "nav.tickets", icon: Ticket },
  { to: "/help", labelKey: "nav.helpCenter", icon: LifeBuoy },
  { to: "/automation", labelKey: "nav.automation", icon: Bot },
  { to: "/outbound", labelKey: "nav.outbound", icon: Send },
  { to: "/reports", labelKey: "nav.reports", icon: BarChart3 },
  { to: "/settings", labelKey: "nav.settings", icon: Settings, end: true },
  { to: "/project-settings", labelKey: "nav.projectSettings", icon: SlidersHorizontal },
];

export function NavSidebar() {
  const { t } = useTranslation();
  const { me } = useSession();
  const { pathname } = useLocation();
  const { projects, activeProjectId, setActiveProjectId } = useProjects();
  const isViewer = me?.activeCompany?.role === "viewer";

  const projectRowClass = (active: boolean) =>
    `flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-body transition-colors ${
      active ? "bg-nav-active font-semibold text-nav-active-fg" : "text-body hover:bg-hairline"
    }`;

  return (
    <nav className="flex w-[230px] shrink-0 flex-col border-r border-border bg-sidebar">
      <div className="flex items-center gap-1 p-2">
        <div className="min-w-0 flex-1">
          <CompanySwitcher />
        </div>
        <NotificationBell />
      </div>

      <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-2">
        <ul className="flex flex-col gap-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-body transition-colors ${
                      isActive || item.alsoActive?.some((p) => pathname.startsWith(p))
                        ? "bg-nav-active font-semibold text-nav-active-fg"
                        : "text-body hover:bg-hairline"
                    }`
                  }
                >
                  <Icon size={17} strokeWidth={2} />
                  {t(item.labelKey)}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="mt-4 flex flex-col gap-1">
          <p className="px-2.5 text-meta font-bold uppercase tracking-wider text-faint">
            {t("nav.projects")}
          </p>
          <ul className="flex flex-col gap-0.5">
            <li>
              <button
                type="button"
                onClick={() => setActiveProjectId(null)}
                className={projectRowClass(activeProjectId === null)}
              >
                <StatusDot tone="muted" size={8} />
                <span className="truncate">{t("nav.allProjects")}</span>
              </button>
            </li>
            {projects.map((project) => (
              <li key={project.id}>
                <button
                  type="button"
                  onClick={() => setActiveProjectId(project.id)}
                  className={projectRowClass(activeProjectId === project.id)}
                >
                  <StatusDot tone="accent" size={8} />
                  <span className="truncate">{project.name}</span>
                </button>
              </li>
            ))}
          </ul>
          {!isViewer ? (
            <button
              type="button"
              className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-body text-muted transition-colors hover:bg-hairline hover:text-ink"
            >
              <Plus size={17} strokeWidth={2} />
              {t("nav.newProject")}
            </button>
          ) : null}
        </div>
      </div>

      <AgentFooter />
    </nav>
  );
}
